/**
 * Doctor dashboard — referrals, care plans, patients & prescriptions.
 * Backend role-filters lists to the calling doctor.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { patientAPI, carePlanAPI, referralAPI, prescriptionAPI } from '../../../services/api';
import Skeleton from '../../../components/common/Skeleton';
import {
  T, FONT, I, firstNameOf, greeting, asList, fmtDate,
  FadeSection, Hero, KpiCard, ChartCard, EmptyState, StatusBadge,
  Avatar, ListRow, QuickActions, PolarStyles,
} from '../shared';

export default function DoctorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const firstName = firstNameOf(user);

  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [carePlans, setCarePlans] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      patientAPI.list({ limit: 50 }).catch(() => null),
      carePlanAPI.list({ limit: 50 }).catch(() => null),
      referralAPI.list({ limit: 50 }).catch(() => null),
      prescriptionAPI.list({ limit: 50 }).catch(() => null),
    ]).then(([p, c, r, rx]) => {
      if (p.status === 'fulfilled') setPatients(asList(p.value));
      if (c.status === 'fulfilled') setCarePlans(asList(c.value));
      if (r.status === 'fulfilled') setReferrals(asList(r.value));
      if (rx.status === 'fulfilled') setPrescriptions(asList(rx.value));
      setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const activePlans = carePlans.filter(c => String(c.status || '').toUpperCase() === 'ACTIVE');
  const pendingReferrals = referrals.filter(r => String(r.status || '').toUpperCase() === 'PENDING');

  if (loading) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <Skeleton width="100%" height={120} style={{ marginBottom: 18, borderRadius: 20 }} />
      <Skeleton.KPIGrid count={4} cols={4} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Skeleton.Card rows={3} /><Skeleton.Card rows={3} />
      </div>
    </div>
  );

  const kpis = [
    { title: 'My Patients', value: patients.length, color: T.primary, Icon: I.heart, sub: 'under your care' },
    { title: 'Active Care Plans', value: activePlans.length, color: T.teal, Icon: I.clipboard, sub: `${carePlans.length} total authored` },
    { title: 'Pending Referrals', value: pendingReferrals.length, color: T.warning, Icon: I.refer, sub: pendingReferrals.length ? 'awaiting action' : 'all reviewed' },
    { title: 'Prescriptions Issued', value: prescriptions.length, color: T.purple, Icon: I.pill, sub: 'total prescribed' },
  ];

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: T.text }}>
      <Hero
        index={0}
        roleLabel="Physician workspace"
        title={`${greeting()}, Dr. ${firstName} ⚕️`}
        subtitle="Your referrals, care plans and patients — everything clinical in one view."
        stats={[
          { label: 'Pending referrals', value: pendingReferrals.length, Icon: I.refer },
          { label: 'Active plans', value: activePlans.length, Icon: I.clipboard },
        ]}
        onRefresh={load}
      />

      <FadeSection index={1} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 18 }}>
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </FadeSection>

      <FadeSection index={2} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, marginBottom: 18 }}>
        <ChartCard
          title="Pending Referrals"
          subtitle="Referrals awaiting acceptance or review"
          action={<button onClick={() => navigate('/referrals')} className="bnv-action-link" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: T.warning, fontFamily: FONT }}>View all →</button>}
        >
          {pendingReferrals.length ? pendingReferrals.slice(0, 6).map((r, i) => (
            <ListRow
              key={r.referral_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.warning}12` }}><I.refer size={16} color={T.warning} /></span>}
              title={r.patient_name || `Referral #${r.referral_id ?? i + 1}`}
              sub={r.reason || (r.created_at ? `Created ${fmtDate(r.created_at)}` : null)}
              right={<StatusBadge value={r.status} />}
            />
          )) : (
            <EmptyState icon={I.refer} color={T.warning} title="No pending referrals" caption="New referrals waiting on your review will appear here." />
          )}
        </ChartCard>

        <ChartCard title="Active Care Plans" subtitle="Plans you authored, with review dates">
          {activePlans.length ? activePlans.slice(0, 6).map((c, i) => (
            <ListRow
              key={c.care_plan_id || i}
              left={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: `${T.teal}12` }}><I.clipboard size={16} color={T.teal} /></span>}
              title={c.title || c.plan_name || c.patient_name || `Care plan #${c.care_plan_id ?? i + 1}`}
              sub={c.review_date ? `Review due ${fmtDate(c.review_date)}` : (c.start_date ? `Started ${fmtDate(c.start_date)}` : null)}
              right={<StatusBadge value={c.status} />}
            />
          )) : (
            <EmptyState icon={I.clipboard} color={T.teal} title="No active care plans" caption="Care plans you author will be tracked here with their review dates." />
          )}
        </ChartCard>

        <ChartCard title="My Patients" subtitle="Patients referred or under your care plans">
          {patients.length ? patients.slice(0, 6).map((p, i) => (
            <div
              key={p.party_id || i}
              onClick={() => p.party_id && navigate(`/patient-360/${p.party_id}`)}
              role="link"
              title="Open Patient 360°"
              style={{ cursor: p.party_id ? 'pointer' : 'default' }}
            >
              <ListRow
                left={<Avatar name={p.name || p.full_name} index={i} />}
                title={p.name || p.full_name || `Patient #${p.party_id ?? i + 1}`}
                sub={p.phone || p.county || null}
                right={<StatusBadge value={p.status || 'ACTIVE'} />}
              />
            </div>
          )) : (
            <EmptyState icon={I.heart} color={T.primary} title="No patients yet" caption="Patients linked to your referrals and care plans will appear here." />
          )}
        </ChartCard>
      </FadeSection>

      <FadeSection index={3}>
        <QuickActions
          navigate={navigate}
          actions={[
            { label: 'Referrals',     path: '/referrals',     color: T.warning, Icon: I.refer },
            { label: 'Care Plans',    path: '/care-plans',    color: T.teal,    Icon: I.clipboard },
            { label: 'Prescriptions', path: '/prescriptions', color: T.purple,  Icon: I.pill },
            { label: 'Analytics',     path: '/analytics',     color: T.info,    Icon: I.chart },
          ]}
        />
      </FadeSection>

      <PolarStyles />
    </div>
  );
}
